/**
 * Layout Preview Component
 *
 * Displays a miniature preview of a layout suggestion with:
 * - Work triangle visualization
 * - Score and metrics
 * - Apply button
 */

import { useMemo } from "react";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { cn } from "@/lib/utils";
import type { LayoutSuggestion } from "@byk/shared";

interface LayoutPreviewProps {
  layout: LayoutSuggestion;
  rank: number;
  isApplied: boolean;
  onApply: () => void;
  /** Compact mode for chat interface (BYK-164) */
  compact?: boolean;
}

// Icons for work triangle points
const POINT_ICONS: Record<string, string> = {
  sink: "🚰",
  fridge: "🧊",
  cooktop: "🔥",
};

// Colors for work triangle visualization
const TRIANGLE_COLORS = {
  optimal: "#22c55e", // green-500
  warning: "#eab308", // yellow-500
  error: "#ef4444", // red-500
};

export const LayoutPreview: React.FC<LayoutPreviewProps> = ({
  layout,
  rank,
  isApplied,
  onApply,
  compact = false,
}) => {
  const { room } = useFloorPlanStore();

  // Calculate preview dimensions (scaled to fit in preview box)
  const previewSize = compact ? 100 : 140;
  const scale = Math.min(previewSize / room.width, previewSize / room.depth);

  // Scale positions for preview
  const scaledPlacements = useMemo(() => {
    return layout.placements.map((p) => ({
      ...p,
      scaledX: p.position.x * scale,
      scaledY: p.position.y * scale,
    }));
  }, [layout.placements, scale]);

  // Get triangle color based on optimality
  const triangleColor = layout.triangle.isOptimal
    ? TRIANGLE_COLORS.optimal
    : layout.score > 0.6
      ? TRIANGLE_COLORS.warning
      : TRIANGLE_COLORS.error;

  // Format perimeter for display
  const perimeterM = (layout.triangle.perimeter / 100).toFixed(1);

  // Get score badge color
  const scoreColor =
    layout.score >= 0.8
      ? "bg-green-100 text-green-700"
      : layout.score >= 0.6
        ? "bg-yellow-100 text-yellow-700"
        : "bg-red-100 text-red-700";

  return (
    <div
      className={cn(
        "layout-preview rounded-lg border bg-white transition-all",
        compact ? "p-2" : "p-3",
        isApplied
          ? "border-green-500 ring-2 ring-green-200"
          : "border-gray-200 hover:border-blue-300 hover:shadow-md"
      )}
      data-testid={`layout-preview-${layout.type.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Header */}
      <div className={cn("flex items-center justify-between", compact ? "mb-1.5" : "mb-2")}>
        <div className="flex items-center gap-2">
          <span className={cn(
            "flex items-center justify-center rounded-full bg-gray-100 font-medium text-gray-600",
            compact ? "h-4 w-4 text-[10px]" : "h-5 w-5 text-xs"
          )}>
            {rank}
          </span>
          <h4 className={cn("font-medium text-gray-900", compact ? "text-xs" : "text-sm")}>{layout.type}</h4>
        </div>
        <span className={cn("rounded-full font-medium", compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs", scoreColor)}>
          {Math.round(layout.score * 100)}%
        </span>
      </div>

      {/* Preview canvas */}
      <div className={cn("relative overflow-hidden rounded-md bg-gray-50", compact ? "mb-2 p-1.5" : "mb-3 p-2")}>
        <svg
          width={previewSize}
          height={previewSize * (room.depth / room.width)}
          viewBox={`0 0 ${room.width * scale} ${room.depth * scale}`}
          className="mx-auto"
          role="img"
          aria-labelledby={`layout-title-${layout.id} layout-desc-${layout.id}`}
        >
          {/* SVG Accessibility: title and description */}
          <title id={`layout-title-${layout.id}`}>{layout.type} Layout Preview</title>
          <desc id={`layout-desc-${layout.id}`}>
            Work triangle visualization showing sink, cooktop, and refrigerator positions.
            Perimeter: {perimeterM} meters. Score: {Math.round(layout.score * 100)}%.
          </desc>

          {/* Room outline */}
          <rect
            x={1}
            y={1}
            width={room.width * scale - 2}
            height={room.depth * scale - 2}
            fill="white"
            stroke="#e5e7eb"
            strokeWidth={2}
          />

          {/* Work triangle */}
          {scaledPlacements.length === 3 && (
            <polygon
              points={scaledPlacements.map((p) => `${p.scaledX},${p.scaledY}`).join(" ")}
              fill={triangleColor}
              fillOpacity={0.15}
              stroke={triangleColor}
              strokeWidth={2}
              strokeDasharray="4 2"
            />
          )}

          {/* Placement points */}
          {scaledPlacements.map((p) => (
            <g key={p.role} transform={`translate(${p.scaledX}, ${p.scaledY})`}>
              {/* Point circle */}
              <circle
                r={compact ? 8 : 12}
                fill="white"
                stroke={triangleColor}
                strokeWidth={compact ? 1.5 : 2}
              />
              {/* Point label */}
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={compact ? 7 : 10}
                className="select-none"
              >
                {POINT_ICONS[p.role]}
              </text>
            </g>
          ))}
        </svg>

        {/* Triangle indicator */}
        <div className={cn("absolute bottom-1 right-1 rounded bg-black/60 text-white", compact ? "px-1 py-0.5 text-[8px]" : "px-1.5 py-0.5 text-[10px]")}>
          {perimeterM}m
        </div>
      </div>

      {/* Metrics - hidden in compact mode */}
      {!compact && (
        <div className="mb-3 grid grid-cols-3 gap-1 text-center text-xs">
          <div className="rounded bg-gray-50 px-1 py-1">
            <div className="font-medium text-gray-700">
              {Math.round(layout.triangle.sinkToFridge)}cm
            </div>
            <div className="text-gray-400">🚰↔🧊</div>
          </div>
          <div className="rounded bg-gray-50 px-1 py-1">
            <div className="font-medium text-gray-700">
              {Math.round(layout.triangle.fridgeToCooktop)}cm
            </div>
            <div className="text-gray-400">🧊↔🔥</div>
          </div>
          <div className="rounded bg-gray-50 px-1 py-1">
            <div className="font-medium text-gray-700">
              {Math.round(layout.triangle.cooktopToSink)}cm
            </div>
            <div className="text-gray-400">🔥↔🚰</div>
          </div>
        </div>
      )}

      {/* Reasoning - truncated in compact mode */}
      <p className={cn("text-gray-500", compact ? "mb-2 text-[10px] line-clamp-1" : "mb-3 text-xs line-clamp-2")}>{layout.reasoning}</p>

      {/* Issues (if any) - hidden in compact mode */}
      {!compact && layout.triangle.issues && layout.triangle.issues.length > 0 && (
        <div className="mb-3 rounded bg-yellow-50 p-2 text-xs text-yellow-700">
          <div className="flex items-start gap-1">
            <span>⚠️</span>
            <ul className="list-inside list-disc space-y-0.5">
              {layout.triangle.issues.slice(0, 2).map((issue, i) => (
                <li key={i}>{issue}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Apply button */}
      <button
        onClick={onApply}
        disabled={isApplied}
        className={cn(
          "w-full rounded-lg font-medium transition-all",
          compact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm",
          isApplied
            ? "cursor-default bg-green-100 text-green-700"
            : "bg-blue-600 text-white hover:bg-blue-700"
        )}
        data-testid={`apply-layout-${layout.type.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {isApplied ? (
          <span className="flex items-center justify-center gap-1">
            <svg className={compact ? "h-3 w-3" : "h-4 w-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Applied
          </span>
        ) : (
          "Apply"
        )}
      </button>
    </div>
  );
};
